import React, { useEffect, useRef, useState } from "react";
import { api, formatErr } from "../../lib/api";
import { MessageCircle, Send, X, Loader2, Sparkles, Tent } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "cc_chat_session";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  // Listen for external "ask" events (e.g., from campsite detail chips)
  useEffect(() => {
    const handler = (e) => {
      const text = e.detail?.text;
      if (!text) return;
      setOpen(true);
      setInput(text);
      // auto-send after the input value has settled
      setTimeout(() => {
        const btn = document.querySelector('[data-testid="chat-send"]');
        if (btn) btn.click();
      }, 120);
    };
    window.addEventListener("cc:chat:ask", handler);
    return () => window.removeEventListener("cc:chat:ask", handler);
  }, []);

  // Load history when session + open
  useEffect(() => {
    if (!open || !sessionId) return;
    (async () => {
      try {
        const { data } = await api.get(`/chat/history?session_id=${encodeURIComponent(sessionId)}`);
        setMessages(data);
      } catch {}
    })();
  }, [open, sessionId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text) return;
    const tempUser = { message_id: `tmp_${Date.now()}`, role: "user", content: text, created_at: new Date().toISOString() };
    setMessages((m) => [...m, tempUser]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/chat/message", { message: text, session_id: sessionId });
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
        try { localStorage.setItem(STORAGE_KEY, data.session_id); } catch {}
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply, content_html: data.reply_html, created_at: new Date().toISOString() }]);
    } catch (e) {
      toast.error(formatErr(e));
      setMessages((m) => m.filter((x) => x.message_id !== tempUser.message_id));
    } finally { setBusy(false); }
  };

  const resetChat = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    setSessionId(null);
    setMessages([]);
  };

  return (
    <>
      {/* Bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-6 z-40 bg-[#ff4500] text-white w-14 h-14 flex items-center justify-center shadow-[0_4px_0_0_#111] hover:bg-[#cc3700] transition-colors"
          data-testid="chat-open">
          <MessageCircle className="w-6 h-6" strokeWidth={1.75} />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-6 z-40 w-[380px] max-w-[calc(100vw-1.5rem)] h-[560px] max-h-[calc(100vh-6rem)] bg-white border hairline-strong shadow-[0_6px_0_0_#111] flex flex-col" data-testid="chat-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b hairline bg-[#111] text-white">
            <div className="flex items-center gap-2">
              <Tent className="w-4 h-4 text-[#ff4500]" strokeWidth={1.75} />
              <div>
                <div className="eyebrow !text-white">Ask Camper</div>
                <div className="text-[11px] mono text-[#ccc]">AI trip assistant</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={resetChat} className="text-[11px] underline text-[#ccc] hover:text-white" data-testid="chat-reset">new</button>
              <button onClick={() => setOpen(false)} data-testid="chat-close"><X className="w-5 h-5" /></button>
            </div>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="p-4 bg-[#fffdfa] border hairline">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-[#ff4500]" strokeWidth={1.5} />
                  <div className="eyebrow">Hello there</div>
                </div>
                <p className="text-sm text-[#333] leading-relaxed">
                  I'm Camper — ask me anything about UK campsites or general camping tips.
                  Try:
                </p>
                <div className="mt-3 space-y-1.5">
                  {[
                    "Cheapest dog-friendly site near Cornwall?",
                    "First time camping — what should I pack?",
                    "Which sites have electric hookups and Wi-Fi?",
                  ].map((q) => (
                    <button key={q} onClick={() => setInput(q)}
                      className="text-left text-xs mono text-[#555] hover:text-[#ff4500] block">
                      → {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={m.message_id || i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`chat-msg-${i}`}>
                <div className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-[#ff4500] text-white" : "bg-[#f5f5f0] text-[#111] border hairline"
                }`}>
                  {m.role === "assistant" && m.content_html ? (
                    <span dangerouslySetInnerHTML={{ __html: m.content_html }} />
                  ) : (
                    m.content
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="px-3 py-2 text-xs bg-[#f5f5f0] border hairline flex items-center gap-2 text-[#555]">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Camper is typing…
                </div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="p-3 border-t hairline flex gap-2" data-testid="chat-form">
            <input className="cc-input flex-1 !p-2 text-sm"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about a campsite, gear, weather…"
              disabled={busy}
              data-testid="chat-input" />
            <button type="submit" className="btn-primary !py-2 !px-3" disabled={busy || !input.trim()} data-testid="chat-send">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
